/**
 * Footer newsletter sign-up.
 *
 * Replaces the Mailchimp integration that shipped with the Sharetribe
 * template. Attractr's audience tooling runs on Brevo, and keeping a second
 * email provider alive purely for the footer box meant contacts collected
 * there landed somewhere nobody ever looked, and Mailchimp had to be declared
 * as a processor for a flow we didn't use.
 *
 * Sign-ups now go to the same Brevo newsletter list as the in-app opt-in
 * prompt (see newsletterPrompt.duck.js), through the existing
 * /api/brevo/add-contact endpoint. The list id is resolved server-side from
 * the 'newsletter' key so it never reaches the browser bundle.
 *
 * The state keys are unchanged from the Mailchimp duck so the Footer keeps
 * working as-is.
 */

import { storableError } from '../util/errors';
import { brevoAPI } from '../util/api';
import { currentLocale } from '../util/locale';

// ================ Action types ================ //

export const SUBSCRIBE_TO_LIST_REQUEST = 'app/newsletterSignup/SUBSCRIBE_TO_LIST_REQUEST';
export const SUBSCRIBE_TO_LIST_SUCCESS = 'app/newsletterSignup/SUBSCRIBE_TO_LIST_SUCCESS';
export const SUBSCRIBE_TO_LIST_ERROR = 'app/newsletterSignup/SUBSCRIBE_TO_LIST_ERROR';

// ================ Reducer ================ //

const initialState = {
  subscribeToListInProgress: false,
  subscribeToListError: null,
  subscribeToListSubmitted: false,
};

export default function newsletterSignupReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SUBSCRIBE_TO_LIST_REQUEST:
      return {
        ...state,
        subscribeToListInProgress: true,
        subscribeToListError: null,
        subscribeToListSubmitted: false,
      };
    case SUBSCRIBE_TO_LIST_SUCCESS:
      return {
        ...state,
        subscribeToListInProgress: false,
        subscribeToListError: null,
        subscribeToListSubmitted: true,
      };
    case SUBSCRIBE_TO_LIST_ERROR:
      return {
        ...state,
        subscribeToListInProgress: false,
        subscribeToListError: payload,
        subscribeToListSubmitted: false,
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const subscribeToListRequest = () => ({ type: SUBSCRIBE_TO_LIST_REQUEST });
export const subscribeToListSuccess = () => ({ type: SUBSCRIBE_TO_LIST_SUCCESS });
export const subscribeToListError = error => ({
  type: SUBSCRIBE_TO_LIST_ERROR,
  payload: error,
  error: true,
});

// ================ Thunks ================ //

/**
 * Add an email to the Brevo newsletter list.
 *
 * @param {Object} params
 * @param {Object} params.subscribingUser - { email, firstName, lastName }
 */
export const subscribeToList = ({ subscribingUser }) => async dispatch => {
  dispatch(subscribeToListRequest());
  try {
    const response = await brevoAPI.addContact({
      firstName: subscribingUser?.firstName || '',
      lastName: subscribingUser?.lastName || '',
      email: subscribingUser?.email,
      lang: currentLocale(),
      createdAt: new Date().toISOString(),
      list: 'newsletter',
    });

    // The endpoint answers 200 with { success: false } when Brevo is
    // unconfigured or rejects the contact, so a bare HTTP check isn't enough.
    const body = response?.data ?? response;
    if (body && body.success === false) {
      throw new Error(body.message || 'Failed to subscribe');
    }

    dispatch(subscribeToListSuccess());
    return body;
  } catch (e) {
    dispatch(subscribeToListError(storableError(e)));
    throw e;
  }
};
